import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSite } from '@/context/SiteContext';
import { SITE_NAME, absUrl, canonicalFor, siteUrlOf } from '@/lib/seo';

/**
 * Per-page <head>: title, description, canonical, Open Graph, Twitter card and JSON-LD.
 * Uses React 19's native document-metadata support: <title>, <meta> and <link> rendered here are hoisted into
 * <head> and removed again when the page unmounts. Defaults come from Admin -> Settings -> SEO.
 */
const SEO_ATTR = { 'data-seo': 'true' };

export const Seo = ({ title, description, path, type = 'website', image, imageAlt, jsonLd = [], noindex = false, article }) => {
  const { site } = useSite();
  const { pathname } = useLocation();
  const s = site?.settings || {};
  const siteName = s.site_name || SITE_NAME;
  const finalTitle = title || s.seo_default_title || `${siteName}`;
  const finalDescription = description || s.seo_default_description || '';
  const canonical = canonicalFor(s, path || pathname);
  // Uploaded photos are served as a 1200px-wide JPG variant (well under 300 KB) so every share card is lightweight.
  const pickImage = image || s.seo_og_image || '/og-default.jpg';
  const ogImage = absUrl(s, /^\/api\/uploads\/.*\.(jpe?g|png|webp)$/i.test(pickImage) ? `${pickImage}?w=1200&fmt=jpg` : pickImage);
  const ogAlt = imageAlt || `${siteName} — hillside resort above the Yellow Bridge, Nusa Lembongan`;
  const scripts = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  // index.html ships a static title / description / Open Graph block as a no-JS fallback. Once React renders, drop
  // those so crawlers and social scrapers (which read the first matching tag) only ever see the per-page values.
  React.useEffect(() => {
    document.querySelectorAll('head > title:not([data-seo]), head meta[name="description"]:not([data-seo]), head meta[property^="og:"]:not([data-seo]), head meta[name^="twitter:"]:not([data-seo])').forEach((el) => el.remove());
  }, []);

  return (
    <>
      <title {...SEO_ATTR}>{finalTitle}</title>
      <meta name="description" content={finalDescription} {...SEO_ATTR} />
      <link rel="canonical" href={canonical} {...SEO_ATTR} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'} {...SEO_ATTR} />
      {s.gsc_verification && (path || pathname) === '/' && <meta name="google-site-verification" content={s.gsc_verification} {...SEO_ATTR} />}

      <meta property="og:type" content={type} {...SEO_ATTR} />
      <meta property="og:site_name" content={siteName} {...SEO_ATTR} />
      <meta property="og:title" content={finalTitle} {...SEO_ATTR} />
      <meta property="og:description" content={finalDescription} {...SEO_ATTR} />
      <meta property="og:url" content={canonical} {...SEO_ATTR} />
      <meta property="og:image" content={ogImage} {...SEO_ATTR} />
      <meta property="og:image:alt" content={ogAlt} {...SEO_ATTR} />
      {!image && <meta property="og:image:width" content="1200" {...SEO_ATTR} />}
      {!image && <meta property="og:image:height" content="630" {...SEO_ATTR} />}
      <meta property="og:locale" content="en_US" {...SEO_ATTR} />
      {article?.published && <meta property="article:published_time" content={article.published} {...SEO_ATTR} />}
      {article?.modified && <meta property="article:modified_time" content={article.modified} {...SEO_ATTR} />}
      {article?.section && <meta property="article:section" content={article.section} {...SEO_ATTR} />}

      <meta name="twitter:card" content="summary_large_image" {...SEO_ATTR} />
      <meta name="twitter:title" content={finalTitle} {...SEO_ATTR} />
      <meta name="twitter:description" content={finalDescription} {...SEO_ATTR} />
      <meta name="twitter:image" content={ogImage} {...SEO_ATTR} />
      <meta name="twitter:image:alt" content={ogAlt} {...SEO_ATTR} />

      {/* JSON-LD may live anywhere in the document; Google reads it from <body> as well as <head>. */}
      {scripts.map((obj, i) => (
        // eslint-disable-next-line react/no-danger
        <script key={i} type="application/ld+json" data-seo="true" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj).replace(/</g, '\\u003c') }} />
      ))}
    </>
  );
};
